import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ChevronRight } from "lucide-react";

/**
 * "Finish your account" — what sign-up no longer asks for (2026-10-28).
 *
 * Sign-up keeps a FixyBee to the minimum that makes them reachable: name,
 * email, phone, ZIP, one trade. The rest is here, each a reason clients
 * choose them, none a lock: the phone code first (a "Phone verified" mark on
 * the public profile, and one tap away), then the face clients choose by, then
 * Stripe Identity, which turns into the verified badge.
 * Each row carries its own state and its own way forward. Stripe Connect is not
 * a row: FixyTerminal is out of scope (CLAUDE.md §5).
 *
 * Nothing here gates anything (the phone only gates a 100% courtesy code, in
 * billing); this card only says where the FixyBee stands.
 */
export default function AccountChecklist({ profile, verifyState }) {
  const { t } = useTranslation();
  const items = [
    {
      key: "phone", done: !!profile.phone_verified, to: "/provider/verify-phone",
      title: t("signupX.itemPhone"), body: t("signupX.itemPhoneBody"), cta: t("signupX.itemPhoneCta"),
    },
    {
      key: "profile", done: !!(profile.avatar_url || profile.business_logo_url) && !!(profile.bio || "").trim(),
      to: "/provider/settings",
      title: t("signupX.itemProfile"), body: t("signupX.itemProfileBody"), cta: t("signupX.itemProfileCta"),
    },
    {
      key: "identity", done: verifyState === "approved", pending: verifyState === "review",
      to: "/provider/verification",
      title: t("signupX.itemIdentity"), body: t("signupX.itemIdentityBody"), cta: t("signupX.itemIdentityCta"),
    },
  ];
  const doneCount = items.filter((i) => i.done).length;
  if (doneCount === items.length) return null;

  return (
    <section className="card-base p-5 mb-8" data-testid="account-checklist">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold text-worker">{t("signupX.checklistTitle")}</h2>
        <span className="font-mono text-xs text-smoke" data-testid="account-checklist-progress">
          {t("signupX.checklistProgress", { done: doneCount, total: items.length })}
        </span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-sand overflow-hidden" aria-hidden="true">
        <div className="h-full bg-hive" style={{ width: `${(doneCount / items.length) * 100}%` }} />
      </div>
      <ol className="mt-4 divide-y divide-sand">
        {items.map((item, n) => (
          <li key={item.key} className="py-3 flex items-center gap-3" data-testid={`checklist-${item.key}`}
              data-done={item.done}>
            <span className={`h-8 w-8 shrink-0 rounded-full inline-flex items-center justify-center font-mono text-sm ${
              item.done ? "bg-worker text-cream" : "border-2 border-sand text-smoke"}`} aria-hidden="true">
              {item.done ? <Check size={16} strokeWidth={3} /> : n + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`font-display font-semibold ${item.done ? "text-smoke line-through" : "text-worker"}`}>{item.title}</p>
              {!item.done && <p className="text-sm text-smoke">{item.body}</p>}
            </div>
            {item.done ? (
              <span className="text-sm text-smoke shrink-0">{t("signupX.done")}</span>
            ) : item.pending ? (
              <span className="badge text-[10px] shrink-0">{t("signupX.inReview")}</span>
            ) : (
              <Link to={item.to} className="shrink-0 inline-flex items-center gap-1 min-h-[44px] px-3 font-display font-semibold text-worker underline"
                    data-testid={`checklist-${item.key}-cta`}>
                {item.cta} <ChevronRight size={16} aria-hidden="true" />
              </Link>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
